/**
 * "TU BOLSA NO ALCANZA PARA TODO" — LEÍDO UNA SOLA VEZ.
 *
 * EL CASO, 2026-08-26. Un cliente armó una hoja de 1.652 renglones con la bolsa a
 * medias. Le costó dos veces y media su plan mensual y nada se lo dijo antes; la primera
 * señal que tuvo fue no poder trabajar al día siguiente.
 *
 * El servidor ahora responde 409 `NO_ALCANZA` antes de encolar nada, diciendo cuántos
 * renglones entran de cuántos. Esto lo traduce a algo que la pantalla pueda usar.
 *
 * POR QUÉ ACÁ Y NO EN CADA PANTALLA. Lo mismo llega desde dos botones distintos —armar
 * la hoja y generar los análisis— y el arreglo de este mismo día fue unificar los DOS
 * porteros que habían crecido por separado en el backend. Repetir acá la lectura sería
 * volver a sembrar el problema del lado del navegador: dos copias que empiezan iguales y
 * terminan diciendo cosas distintas.
 */

export interface BolsaNoAlcanza {
  /** Cuántas unidades entran con lo que le queda. */
  caben: number;
  /** Cuántas pidió. */
  pedidos: number;
  /** El aviso ya redactado por el servidor: habla en renglones, nunca en tokens. */
  mensaje: string;
}

/** Forma mínima del error del cliente HTTP, sin atarse a su clase. */
interface ErrorConCodigo {
  code?: unknown;
  message?: unknown;
  body?: unknown;
}

/**
 * ¿Este error es "no te alcanza para todo"? Devuelve los números, o `null` si es
 * cualquier otra cosa.
 *
 * Solo devuelve algo cuando hay al menos UNA unidad que sí entra: ofrecer "hacer 0 de
 * 200" no es una opción, es una forma rebuscada de decir que no queda nada — y de eso
 * ya se encarga el aviso de bolsa vacía.
 */
export function leerBolsaNoAlcanza(e: unknown): BolsaNoAlcanza | null {
  if (!e || typeof e !== 'object') return null;
  const err = e as ErrorConCodigo;
  if (err.code !== 'NO_ALCANZA') return null;

  const cuerpo = (err.body && typeof err.body === 'object' ? err.body : {}) as Record<string, unknown>;
  const caben = Number(cuerpo.caben);
  const pedidos = Number(cuerpo.pedidos);
  if (!Number.isFinite(caben) || caben < 1) return null;

  return {
    caben,
    pedidos: Number.isFinite(pedidos) && pedidos > 0 ? pedidos : 0,
    mensaje: typeof err.message === 'string' && err.message
      ? err.message
      : 'No te alcanza para todo lo que pediste.',
  };
}

/** Lo que dice el botón de confirmar, con el número adentro. */
export function etiquetaDeContinuar(v: BolsaNoAlcanza, unidad: 'renglones' | 'análisis'): string {
  return `Continuar con ${v.caben.toLocaleString('es-MX')} ${unidad}`;
}
